import { Alert, AlertIcon, AlertTitle, Box, Button, HStack, Icon, Image, Modal, ModalBody, ModalContent, ModalOverlay, Stack, Text, useDisclosure, useOutsideClick } from '@chakra-ui/react'
import { faPaperPlane, faRefresh } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EmailSenderPreview, InputFormControl } from 'BrokerTsLib/src/components'
import type { Offer, SendCalculationEmailRequest } from 'BrokerTsLib/src/dtos'
import { useCustomToast } from 'BrokerTsLib/src/hooks/useCustomToast'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { noop } from 'lodash'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import root from 'react-shadow'
import * as yup from 'yup'

import { Loading } from '@/components/Loading'
import { TextEditor } from '@/components/TextEditor'
import { useCarkulkaOffersContext } from '@/context/CarkulkaOffersProvider'
import { useCestovkoOffersContext } from '@/context/CestovkoOffersProvider'
import type { IOffersContext } from '@/context/OffersProvider'
import { usePropertyOffersContext } from '@/context/PropertyOffersProvider'
import type { GetCalculationEmailResponse } from '@/hooks/useMailing'
import { useGetCalculationEmail, useSendCalculationEmail } from '@/hooks/useMailing'
import { useInsuranceParamsContext } from '@/layouts/Wizard'

interface SendCalculationContextProps {
  paramsId?: string
  offerIds: number[]
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  isDisabled: boolean
  order?: number
}

const SendCalculationContext = createContext<SendCalculationContextProps | null>(null)

function SendCalculationContextProvider<T extends Offer>({ children, context, order }: { children: ReactNode, context: () => IOffersContext<T>, order?: number }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { insuranceParams } = useInsuranceParamsContext()
  const { offers } = context()

  const offerIds = offers?.filter(x => x.included).map((o) => o.offerId) ?? []

  const isDisabled = offers?.every((o) => o.included === false || o.errors.some(x => x.softError === false)) ?? true

  return <SendCalculationContext.Provider value={{
    paramsId: insuranceParams?.generalParams.paramsId ?? undefined,
    offerIds,
    isOpen,
    onOpen,
    onClose,
    isDisabled,
    order
  }}>
    {children}
  </SendCalculationContext.Provider>
}

export const SendCalculationVehicleContextProvider = ({ children, order }: { children: ReactNode, order?: number }) => SendCalculationContextProvider({ children, context: useCarkulkaOffersContext, order })

export const SendCalculationPropertyContextProvider = ({ children, order }: { children: ReactNode, order?: number }) => SendCalculationContextProvider({ children, context: usePropertyOffersContext, order })

export const SendCalculationTravelContextProvider = ({ children, order }: { children: ReactNode, order?: number }) => SendCalculationContextProvider({ children, context: useCestovkoOffersContext, order })

export const useSendCalculationContext = () => {
  const context = useContext(SendCalculationContext)
  if (!context) {
    throw new Error('useSendCalculationContext must be used within a SendCalculationProvider')
  }
  return context
}

interface GetCalculationVehicleEmailResponse extends GetCalculationEmailResponse {
  offers: string
}

export const SendCalculationModal = () => {
  const { paramsId, offerIds, isOpen, onClose } = useSendCalculationContext()
  const { data, isLoading, isSuccess, isError, refetch } = useGetCalculationEmail<GetCalculationVehicleEmailResponse>(paramsId, false)
  const { mutateAsync: sendAsync } = useSendCalculationEmail()
  const { successToast, errorToast } = useCustomToast()

  useEffect(() => {
    if (isOpen)
      refetch()
  }, [isOpen, offerIds, refetch])

  const send = useCallback((v: SendCalculationFormValues) => sendAsync({ ...v, paramsId: Number(paramsId), insurerOfferIds: offerIds })
    .then(r => {
      onClose()
      successToast('E-mail byl úspěšně odeslán')(r)
    })
    .catch(errorToast('Nepodařilo se odeslat e-mail'))
    , [sendAsync, paramsId, offerIds, errorToast, onClose, successToast])

  const bodyRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: bodyRef,
    handler: (e) => {
      const clickedElementIsInsideContent = bodyRef.current?.contains(e.target as Node)
      const isChildOfJodit = (child: Node | null): boolean => {
        let node = child
        while (node) {
          if (node instanceof HTMLElement && node.classList.contains('jodit')) {
            return true
          }
          node = node.parentNode
        }
        return false
      }

      if (!clickedElementIsInsideContent && !isChildOfJodit(e.target as Node)) {
        onClose()
      }
    }
  })

  return <Modal isOpen={isOpen} onClose={noop} size='full'>
    <ModalOverlay />
    <ModalContent bg='transparent' boxShadow='none'>
      <ModalBody px={{ base: 2, md: 4 }} py={{ base: 2, md: 6 }}>
        <Box ref={bodyRef} mx='auto' w='100%' maxW={737}>
          {isLoading && <Loading mt={14} />}
          {isSuccess && <SendCalculationForm {...data} onSubmit={send} />}
          {isError && <Alert status="error" mt={14} as={Stack} alignContent='start' >
            <HStack>
              <AlertIcon />
              <AlertTitle>
                Nepodařilo se načíst data.
              </AlertTitle>
            </HStack>
          </Alert>}
        </Box>
      </ModalBody>
    </ModalContent>
  </Modal >
}

const schema = yup.object({
  email: yup.string().required('Je nutné vyplnit e-mailovou adresu příjemce.').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Neplatná e-mailová adresa.'),
  header: yup.string(),
  footer: yup.string(),
})

type SendCalculationFormValues = Required<Pick<SendCalculationEmailRequest, 'email'>> & Omit<SendCalculationEmailRequest, 'paramsId' | 'insurerOfferIds' | 'email'>

const SendCalculationForm = ({ email, subject, logoUrl, header, offers, footer, signature, disclaimer, senderName, senderAddress, replyAddress, onSubmit }: {
  email?: string
  subject?: string
  logoUrl: string
  header: string
  offers?: string
  footer: string
  signature: string
  disclaimer: string
  senderName?: string
  senderAddress?: string
  replyAddress?: string
  onSubmit: (data: SendCalculationFormValues) => Promise<void>
}) => {
  const methods = useForm<SendCalculationFormValues>({
    defaultValues: {
      email: email ?? '',
      header: header ?? '',
      footer: footer ?? ''
    },
    resolver: yupResolver(schema),
  })

  const { getValues, setValue, handleSubmit, formState: { isSubmitting, isValid }, reset } = methods

  useEffect(() => {
    reset({
      email: email ?? '',
      header: header ?? '',
      footer: footer ?? ''
    })
  }, [email, header, footer, reset])

  return (
    <FormProvider {...methods}>
      <Stack as="form" gap={{ base: 3, md: 6 }} onSubmit={handleSubmit(onSubmit)}>
        <Stack padding="16px 24px 24px 24px" bg='white' borderRadius='10px' boxShadow='0 0 16px 0 rgba(0, 0, 0, 0.1)' border="1px solid #e5e7e9;">
          <HStack w='full' gap={6} alignItems='end'>
            <InputFormControl name="email"
              label="E-mailová adresa příjemce"
              placeholder='jan.novak@email.cz'
              isRequired
              w='full'
              flexGrow={1} />
            <Button colorScheme="primary" type="submit" isLoading={isSubmitting} px={8} isDisabled={!isValid}>
              <HStack gap={4}>
                <Icon as={FontAwesomeIcon} icon={faPaperPlane} />
                <span>Odeslat</span>
              </HStack>
            </Button>
          </HStack>
          <Text fontSize='xs' color='gray.500'>
            Na e-mailovou adresu příjemce bude odeslána zpráva s vybranými kalkulacemi.<br />
            V náhledu níže můžete zprávu před odesláním upravit a zkontrolovat.
          </Text>
          <EmailSenderPreview senderName={senderName} senderAddress={senderAddress} replyAddress={replyAddress} subject={subject} />
        </Stack>

        <Stack gap={0} padding="16px 24px 24px 24px" bg='white' borderRadius='10px' boxShadow='0 0 16px 0 rgba(0, 0, 0, 0.1)' border="1px solid #e5e7e9;" pos='relative'>
          <Button position='absolute' right={4} top={4} variant='ghost' size='sm' onClick={() => reset()} color='gray'>
            <HStack>
              <Icon as={FontAwesomeIcon} icon={faRefresh} />
              <span>Vrátit změny</span>
            </HStack>
          </Button>

          <Box textAlign='center' pb={8} pt={2.5}>
            <Image src={logoUrl} alt="Logo" maxW={170} />
          </Box>

          <Box pb={6} m={-1.5}>
            <TextEditor
              toolbar={false}
              value={getValues('header') ?? ''}
              onBlur={(newValue) => setValue('header', newValue)}
              onChange={(newValue) => setValue('header', newValue)}
            />
          </Box>

          {offers &&
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: offers }} />
            </root.div>
          }

          <Box m={-1.5} pt={2}>
            <TextEditor
              toolbar={false}
              minHeight="50px"
              value={getValues('footer') ?? ''}
              onBlur={(newValue) => setValue('footer', newValue)}
              onChange={(newValue) => setValue('footer', newValue)}
            />
          </Box>

          <Box py={6}>
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: signature }} />
            </root.div>
          </Box>


          <Box margin='0 -24px -24px -24px' bg='#f2f4f8' padding="16px 24px" borderRadius='0 0 10px 10px'>
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: disclaimer }} />
            </root.div>
          </Box>
        </Stack>
      </Stack>
    </FormProvider>
  )
}